/**
 * Wire <-> app mapping for exercises (step 1: activity types).
 *
 * Every field name the backend contract may still rename lives in this file:
 * reads accept camelCase (the API convention) and the snake_case names used in
 * the contract notes; writes use camelCase. A missing field falls back to a
 * strength exercise, so responses from a backend without step 1 still work.
 */
import {
  EXERCISE_TYPES,
  calendarTypes,
  isExerciseType,
  normalizeType,
  rowType,
  type ExerciseType,
} from "../lib/activity";
import {
  MAX_PROGRESS_POINTS,
  type ProgressPoint,
  type ProgressRange,
  type ProgressSeries,
} from "../lib/progress";
import { isRecordKind, normalizeKinds, toCanonical, RECORD_KINDS, type PersonalRecord, type RecordKind } from "../lib/records";
import type {
  CalendarResponse,
  CatalogExercise,
  CompletedWorkout,
  Exercise,
  ExerciseHistoryItem,
  MonthStats,
  MonthTypeStat,
  PlannedWorkout,
  Profile,
  WorkoutTemplate,
} from "./types";

type Raw = Record<string, unknown>;

const rec = (v: unknown): Raw => (v && typeof v === "object" ? (v as Raw) : {});
const pick = (raw: Raw, camel: string, snake: string) => raw[camel] ?? raw[snake];
const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/** The single place that knows how the catalog says "this exercise has records". */
export function readHasHistory(raw: Raw): boolean | undefined {
  const v = pick(raw, "hasHistory", "has_history");
  return typeof v === "boolean" ? v : undefined;
}

export function readExercise(input: unknown): Exercise {
  const raw = rec(input);
  const values = {
    sets: num(raw.sets),
    reps: num(raw.reps),
    weight: num(raw.weight),
    durationSec: num(pick(raw, "durationSec", "duration_sec")),
    distanceM: num(pick(raw, "distanceM", "distance_m")),
    intensity: num(raw.intensity),
    rounds: num(raw.rounds),
  };
  const explicit = pick(raw, "exerciseType", "exercise_type");
  const { newRecords: _nr, new_records: _nrs, ...plain } = raw;
  const beaten = normalizeKinds(pick(raw, "newRecords", "new_records"));
  return {
    ...(plain as unknown as Exercise),
    ...values,
    ...(beaten.length ? { newRecords: beaten } : {}),
    // The app model keeps numeric sets/reps; non-strength rows never show them.
    sets: values.sets ?? 1,
    reps: values.reps ?? 1,
    // Rows without an explicit type: infer from their values (no values = strength).
    exerciseType: explicit != null ? normalizeType(explicit) : rowType(values),
    icon: typeof raw.icon === "string" && raw.icon ? raw.icon : null,
  };
}

export function readCatalogExercise(input: unknown): CatalogExercise {
  const raw = rec(input);
  const ex = raw as unknown as CatalogExercise;
  return {
    ...ex,
    muscleGroups: Array.isArray(raw.muscleGroups) ? ex.muscleGroups : [],
    exerciseType: normalizeType(pick(raw, "exerciseType", "exercise_type")),
    icon: typeof raw.icon === "string" && raw.icon ? raw.icon : null,
    hasHistory: readHasHistory(raw),
  };
}

type WithExercises = { exercises?: unknown };

function readWorkout<T extends WithExercises>(w: T): T {
  if (!w || typeof w !== "object") return w;
  const list = Array.isArray(w.exercises) ? w.exercises : [];
  return { ...w, exercises: list.map(readExercise) };
}

export const readCompleted = (w: CompletedWorkout) => readWorkout(w);
export const readPlanned = (w: PlannedWorkout) => readWorkout(w);
export const readTemplate = (w: WorkoutTemplate) => readWorkout(w);

export function readCalendar(c: CalendarResponse): CalendarResponse {
  if (!c || typeof c !== "object") return c;
  const days: CalendarResponse["days"] = {};
  for (const [key, day] of Object.entries(c.days ?? {})) {
    const types = calendarTypes(pick(rec(day), "exerciseTypes", "exercise_types"));
    days[key] = {
      completed: (day?.completed ?? []).map(readCompleted),
      planned: (day?.planned ?? []).map(readPlanned),
      // Missing or empty = old backend / legacy data: the day keeps its plain look.
      ...(types.length ? { exerciseTypes: types } : {}),
    };
  }
  return { ...c, days };
}

/** History rows: the step-1 value fields next to sets x reps x weight. */
export function readHistoryItem(input: unknown): ExerciseHistoryItem {
  const raw = rec(input);
  const explicit = pick(raw, "exerciseType", "exercise_type");
  return {
    ...(raw as unknown as ExerciseHistoryItem),
    durationSec: num(pick(raw, "durationSec", "duration_sec")),
    distanceM: num(pick(raw, "distanceM", "distance_m")),
    intensity: num(raw.intensity),
    rounds: num(raw.rounds),
    ...(explicit != null ? { exerciseType: normalizeType(explicit) } : {}),
  };
}

/**
 * Write payload of one exercise row. Only the fields of the row's type are
 * sent; the server takes the type from the catalog entry (catalogExerciseId or name).
 */
export function writeExercise(ex: Exercise, index = 0): Raw {
  const type: ExerciseType = normalizeType(ex.exerciseType);
  const out: Raw = {
    name: ex.name,
    sortOrder: ex.sortOrder ?? index,
    muscleGroups: ex.muscleGroups ?? [],
    notes: ex.notes ?? null,
    catalogExerciseId: ex.catalogExerciseId ?? null,
  };
  switch (type) {
    case "strength":
      out.sets = ex.sets;
      out.reps = ex.reps;
      out.weight = ex.weight ?? null;
      break;
    case "timed":
      out.durationSec = ex.durationSec ?? null;
      break;
    case "distance":
      out.distanceM = ex.distanceM ?? null;
      out.durationSec = ex.durationSec ?? null;
      break;
    case "session":
      out.durationSec = ex.durationSec ?? null;
      out.intensity = ex.intensity ?? null;
      out.rounds = ex.rounds ?? null;
      break;
  }
  return out;
}

/** Map `body.exercises` (if any) to the wire format; other fields pass through. */
export function withWireExercises(body: unknown): unknown {
  const b = rec(body);
  if (!Array.isArray(b.exercises)) return body;
  return {
    ...b,
    exercises: (b.exercises as Exercise[]).map((ex, i) => writeExercise(ex, i)),
  };
}

// ── step 3: weekly goal in minutes ──────────────────────────────────────────

/**
 * Profile: `weeklyGoalMinutes` (number | null) sits on the profile, `weeklyMinutes`
 * on the weekly progress object (`weeklyGoal`). A backend without step 3 sends
 * neither, and the result then has neither key, so the minutes UI stays hidden.
 */
export function readProfile(input: Profile): Profile {
  if (!input || typeof input !== "object") return input;
  const raw = rec(input);
  const { weeklyGoalMinutes: _g, weeklyMinutes: _m, ...rest } = raw;
  const out = rest as unknown as Profile;

  const goalKey = "weeklyGoalMinutes" in raw ? "weeklyGoalMinutes" : "weekly_goal_minutes";
  if (goalKey in raw) {
    const g = num(raw[goalKey]);
    out.weeklyGoalMinutes = g != null && g > 0 ? Math.round(g) : null;
  }
  const weekly = rec(pick(raw, "weeklyGoal", "weekly_goal"));
  const done = num(pick(weekly, "weeklyMinutes", "weekly_minutes") ?? pick(raw, "weeklyMinutes", "weekly_minutes"));
  if (done != null && done >= 0) out.weeklyMinutes = done;
  return out;
}

/** Body of the weekly goal write: only the keys that are set (omitted = keep, null = clear). */
export function writeWeeklyGoal(changes: {
  targetCount?: number | null;
  weeklyGoalMinutes?: number | null;
}): Raw {
  const out: Raw = {};
  if (changes.targetCount !== undefined) out.targetCount = changes.targetCount;
  if (changes.weeklyGoalMinutes !== undefined) out.weeklyGoalMinutes = changes.weeklyGoalMinutes;
  return out;
}

// ── step 4: personal records ────────────────────────────────────────────────

/** Path of the records endpoint: the only place that knows it. */
export const recordsPath = (exerciseId: string) => `/api/catalog/exercises/${encodeURIComponent(exerciseId)}/records`;

/**
 * Records response `{ records: [{ kind, value, unit?, date, workoutId }] }` (a bare array
 * is accepted too). Values are normalized to kg / seconds / meters by `unit`; rows with an
 * unknown kind, a bad value or an unknown unit are dropped, one row per kind (first wins),
 * result in display order. Anything unexpected gives [] and the records UI stays hidden.
 */
export function readRecords(input: unknown): PersonalRecord[] {
  const list = Array.isArray(input) ? input : rec(input).records;
  if (!Array.isArray(list)) return [];
  const byKind = new Map<string, PersonalRecord>();
  for (const item of list) {
    const raw = rec(item);
    if (!isRecordKind(raw.kind) || byKind.has(raw.kind)) continue;
    const value = toCanonical(raw.kind, Number(raw.value), raw.unit);
    if (value == null) continue;
    const date = pick(raw, "date", "achievedAt") ?? pick(raw, "achieved_at", "performedAt");
    const wid = pick(raw, "workoutId", "workout_id");
    byKind.set(raw.kind, {
      kind: raw.kind,
      value,
      date: typeof date === "string" ? date : null,
      workoutId: typeof wid === "string" || typeof wid === "number" ? String(wid) : null,
    });
  }
  return RECORD_KINDS.flatMap((k) => byKind.get(k) ?? []);
}

// ── step 6: exercise progress ───────────────────────────────────────────────

/** Path of the progress endpoint: the only place that knows it (and the `range` values). */
const PROGRESS_RANGE_PARAM: Record<ProgressRange, string> = { "3m": "3m", "1y": "1y", all: "all" };
export const progressPath = (exerciseId: string, range: ProgressRange) =>
  `/api/catalog/exercises/${encodeURIComponent(exerciseId)}/progress?range=${PROGRESS_RANGE_PARAM[range]}`;

/** "estOneRepMax", "est_one_rep_max", "max-duration" -> the record kind. */
function progressKind(v: unknown): RecordKind | null {
  if (typeof v !== "string") return null;
  const flat = v.replace(/[_\-\s]/g, "").toLowerCase();
  return RECORD_KINDS.find((k) => k.toLowerCase() === flat) ?? null;
}

/**
 * Progress response `{ kind, points: [{ date, value, unit? }], unit? }` (a bare array of
 * points is accepted too, then `fallbackKind` says what is plotted). Points with a bad date
 * or a non-finite / non-positive value are dropped, the rest is sorted by date ascending
 * (stable) and cut to the newest 200. Values are normalized to kg / seconds / meters by the
 * point's or the response's `unit`. Anything that is not a progress response gives null and
 * the chart stays hidden; a valid response with no points is a series with no points.
 */
export function readProgress(input: unknown, fallbackKind: RecordKind | null = null): ProgressSeries | null {
  const top = rec(input);
  const list = Array.isArray(input) ? input : (top.points ?? top.data ?? top.series);
  if (!Array.isArray(list)) return null;
  const kind = progressKind(top.kind ?? top.metric) ?? fallbackKind;
  if (!kind) return null;
  const topUnit = top.unit;
  const points: ProgressPoint[] = [];
  for (const item of list) {
    const raw = rec(item);
    const date = raw.date ?? pick(raw, "performedAt", "performed_at") ?? raw.day;
    if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(date)) continue;
    const value = toCanonical(kind, Number(raw.value ?? raw.y), raw.unit ?? topUnit);
    if (value == null) continue;
    points.push({ date, value });
  }
  const indexed = points.map((p, i) => [p, i] as const);
  indexed.sort((a, b) => (a[0].date < b[0].date ? -1 : a[0].date > b[0].date ? 1 : a[1] - b[1]));
  return { kind, points: indexed.map(([p]) => p).slice(-MAX_PROGRESS_POINTS) };
}

// ── step 5: month stats by activity type ────────────────────────────────────

/**
 * `byType: [{ type, count, minutes, distanceMeters? }]` (snake_case `by_type`,
 * `distance_meters` accepted). Unknown types and rows without a positive count are
 * dropped, repeated types are summed, result in fixed type order. A backend without
 * step 5 (or garbage) gives [] and the block stays hidden.
 */
export function readByType(input: unknown): MonthTypeStat[] {
  const raw = rec(input);
  const list = pick(raw, "byType", "by_type");
  if (!Array.isArray(list)) return [];
  const acc = new Map<ExerciseType, MonthTypeStat>();
  for (const item of list) {
    const r = rec(item);
    const type = r.type ?? r.exerciseType;
    if (!isExerciseType(type)) continue;
    const count = num(r.count);
    if (count == null || count <= 0) continue;
    const minutes = num(r.minutes ?? r.totalMinutes ?? r.durationMinutes) ?? 0;
    const dist = num(pick(r, "distanceMeters", "distance_meters")) ?? 0;
    const row = acc.get(type) ?? { type, count: 0, minutes: 0 };
    row.count += Math.round(count);
    row.minutes += Math.max(0, minutes);
    if (type === "distance" && dist > 0) row.distanceMeters = (row.distanceMeters ?? 0) + dist;
    acc.set(type, row);
  }
  return EXERCISE_TYPES.flatMap((t) => acc.get(t) ?? []);
}

/** Month stats with the step 5 rows normalized; every other field passes through. */
export function readMonthStats(input: MonthStats): MonthStats {
  if (!input || typeof input !== "object") return input;
  const { byType: _a, by_type: _b, ...rest } = input as unknown as Raw;
  return { ...(rest as unknown as MonthStats), byType: readByType(input) };
}
